import { type Dispatch, type SetStateAction, useContext } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import { type ReimbursementsFilters } from 'components/hooks/reimbursements/useGetReimbursementRequests'
import { Button } from 'slab'
import { type BrokersBroker } from 'utils/web_api.schemas'

type ReimbursementsQuickFiltersProps = {
  filters: ReimbursementsFilters
  setFilters: Dispatch<SetStateAction<ReimbursementsFilters>>
}

// The 5 queries below mimic the Ops NAV dashboard queries for certain sets of issues. These filters
// overwrite the existing query entirely.
function setWaitingForReview (
  setFilters: Dispatch<SetStateAction<ReimbursementsFilters>>
) {
  setFilters(() => ({
    states: ['waiting_for_review'],
  }))
}

// These queries below are for the filters that are not included in the Ops NAV dashboard queries.
// They do not overwrite the existing query, but rather add to it to augment the existing query for better triaging.
function setAssignedToMeFilters (
  setFilters: Dispatch<SetStateAction<ReimbursementsFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  setFilters((prev) => ({
    ...prev,
    ...(currentBroker ? { broker_ids: [currentBroker?.id] } : {}),
  }))
}

function setUnassignedFilters (
  setFilters: Dispatch<SetStateAction<ReimbursementsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    broker_ids: ['unassigned'],
  }))
}

function setVipEmployerFilters (
  setFilters: Dispatch<SetStateAction<ReimbursementsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    vip_employer: 'true',
  }))
}

const ReimbursementsQuickFilters = ({
  setFilters,
}: ReimbursementsQuickFiltersProps) => {
  const { currentBroker } = useContext(BrokerContext)

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>
      <Button
        variant='tertiary'
        onClick={() => setWaitingForReview(setFilters)}
      >
        Waiting For Review
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setAssignedToMeFilters(setFilters, currentBroker)}
      >
        Assigned to me
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setUnassignedFilters(setFilters)}
      >
        Unassigned
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setVipEmployerFilters(setFilters)}
      >
        VIP Employers
      </Button>
    </div>
  )
}

export default ReimbursementsQuickFilters
